class CurrencyOptimizer {
  constructor() {
    // Your top currencies in order of sales volume
    this.priorityCurrencies = [
      'USD', // United States
      'GBP', // United Kingdom
      'EUR', // Germany + EU countries
      'CAD', // Canada
      'JPY', // Japan
      'AUD', // Australia
      'MXN', // Mexico
      'CZK'  // Czech Republic (home market)
    ];
    
    // Currency display names (override country names)
    this.currencyLabels = {
      'USD': 'USD $ - United States',
      'GBP': 'GBP £ - United Kingdom',
      'EUR': 'EUR € - European Union',
      'CAD': 'CAD $ - Canada',
      'JPY': 'JPY ¥ - Japan',
      'AUD': 'AUD $ - Australia',
      'MXN': 'MXN $ - Mexico',
      'CZK': 'CZK Kč - Czech Republic'
    };
    
    this.allCurrencyItems = [];
    this.init();
  }

  init() {
    // Wait for localization dropdowns to be in DOM
    this.waitForDropdowns();
  }

  waitForDropdowns() {
    const dropdown = document.querySelector('dropdown-localization');
    if (dropdown) {
      this.optimizeCountrySelector();
    } else {
      setTimeout(() => this.waitForDropdowns(), 100);
    }
    
    // Also watch for dynamic updates
    const observer = new MutationObserver(() => {
      this.optimizeCountrySelector();
    });
    
    const topbar = document.querySelector('.topbar');
    if (topbar) {
      observer.observe(topbar, { childList: true, subtree: true });
    }
  }

  optimizeCountrySelector() {
    // Find the country selector dropdown
    const countryDropdown = document.querySelector('[id^="CountryLocalization-"]');
    if (!countryDropdown) return;
    
    const countryList = countryDropdown.querySelector('ul[role="list"]');
    if (!countryList || countryList.dataset.optimized === 'true') return;
    
    // Mark as optimized to prevent re-processing
    countryList.dataset.optimized = 'true';
    
    // Get all country items (don't remove them from DOM yet)
    const allItems = Array.from(countryList.querySelectorAll('li'));
    
    // Group countries by currency
    const currencyGroups = this.groupByCurrency(allItems);
    
    // Add search input before the list
    this.addSearchInput(countryDropdown, countryList);
    
    // Now we'll reorder the list without destroying elements
    const fragment = document.createDocumentFragment();
    
    // Add priority currencies first
    this.priorityCurrencies.forEach(currencyCode => {
      if (currencyGroups[currencyCode]) {
        const countries = currencyGroups[currencyCode];
        // Use the first country's original element
        const primaryItem = countries[0].originalItem;
        const link = primaryItem.querySelector('a');
        
        // Update the display text
        const displayLabel = this.currencyLabels[currencyCode] || countries[0].fullText;
        if (link) {
          link.textContent = displayLabel;
          link.setAttribute('title', displayLabel);
        }
        
        // Mark as used
        primaryItem.dataset.currencyGroup = currencyCode;
        
        // Add to fragment
        fragment.appendChild(primaryItem);
        
        this.allCurrencyItems.push({
          element: primaryItem,
          currency: currencyCode,
          searchText: displayLabel
        });
      }
    });
    
    // Add separator
    const separator = document.createElement('li');
    separator.className = 'currency-separator opacity-50 py-2';
    separator.innerHTML = '<hr style="border-color: var(--color-foreground); opacity: 0.2;">';
    fragment.appendChild(separator);
    
    // Add remaining currencies
    const otherCurrencies = Object.keys(currencyGroups)
      .filter(code => !this.priorityCurrencies.includes(code))
      .sort();
    
    otherCurrencies.forEach(currencyCode => {
      const countries = currencyGroups[currencyCode];
      const primaryItem = countries[0].originalItem;
      const link = primaryItem.querySelector('a');
      
      const displayLabel = this.currencyLabels[currencyCode] || countries[0].fullText;
      if (link) {
        link.textContent = displayLabel;
        link.setAttribute('title', displayLabel);
      }
      
      primaryItem.dataset.currencyGroup = currencyCode;
      fragment.appendChild(primaryItem);
      
      this.allCurrencyItems.push({
        element: primaryItem,
        currency: currencyCode,
        searchText: displayLabel
      });
    });
    
    // Clear and re-append in new order
    countryList.innerHTML = '';
    countryList.appendChild(fragment);
  }

  addSearchInput(dropdown, list) {
    // Check if search already exists
    if (dropdown.querySelector('.currency-search')) return;
    
    // Create search container
    const searchContainer = document.createElement('div');
    searchContainer.className = 'currency-search';
    searchContainer.style.cssText = `
      padding: 12px;
      border-bottom: 1px solid rgba(var(--color-foreground), 0.1);
      position: sticky;
      top: 0;
      background: rgba(var(--color-background), 1);
      z-index: 10;
    `;
    
    // Create search input
    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.placeholder = 'Search currency...';
    searchInput.className = 'currency-search-input';
    searchInput.style.cssText = `
      width: 100%;
      padding: 8px 12px;
      border: 1px solid rgba(var(--color-foreground), 0.2);
      border-radius: 4px;
      font-size: 14px;
      background: transparent;
      color: rgb(var(--color-foreground));
      outline: none;
    `;
    
    // Prevent dropdown from closing when clicking/focusing the search input
    searchContainer.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    
    searchContainer.addEventListener('mousedown', (e) => {
      e.stopPropagation();
    });
    
    searchInput.addEventListener('focus', (e) => {
      e.stopPropagation();
    });
    
    // Add input event listener
    searchInput.addEventListener('input', (e) => {
      this.filterCurrencies(e.target.value);
    });
    
    // Clear search when dropdown closes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'aria-expanded') {
          const button = dropdown.closest('.localization__item')?.querySelector('button');
          if (button && button.getAttribute('aria-expanded') === 'false') {
            setTimeout(() => {
              searchInput.value = '';
              this.filterCurrencies('');
            }, 300);
          }
        }
      });
    });
    
    const button = dropdown.closest('.localization__item')?.querySelector('button');
    if (button) {
      observer.observe(button, { attributes: true });
    }
    
    searchContainer.appendChild(searchInput);
    
    // Insert before the list
    const container = dropdown.querySelector('.dropdown__container');
    if (container) {
      const nav = container.querySelector('.dropdown__nav');
      if (nav) {
        container.insertBefore(searchContainer, nav);
      }
    }
    
    // Auto-focus the search input when dropdown opens
    const localizationButton = dropdown.closest('.localization__item')?.querySelector('button');
    if (localizationButton) {
      localizationButton.addEventListener('click', () => {
        setTimeout(() => {
          searchInput.focus();
        }, 100);
      });
    }
  }

  filterCurrencies(searchTerm) {
    const term = searchTerm.toLowerCase().trim();
    let visibleCount = 0;
    const separator = document.querySelector('.currency-separator');
    
    this.allCurrencyItems.forEach(item => {
      const matches = !term || 
        item.currency.toLowerCase().includes(term) ||
        item.searchText.toLowerCase().includes(term);
      
      if (matches) {
        item.element.style.display = '';
        visibleCount++;
      } else {
        item.element.style.display = 'none';
      }
    });
    
    // Hide separator if searching
    if (separator) {
      separator.style.display = term ? 'none' : '';
    }
    
    // Show "no results" message
    this.showNoResults(visibleCount === 0);
  }

  showNoResults(show) {
    const dropdown = document.querySelector('[id^="CountryLocalization-"]');
    if (!dropdown) return;
    
    let noResults = dropdown.querySelector('.no-results-message');
    
    if (show && !noResults) {
      noResults = document.createElement('li');
      noResults.className = 'no-results-message opacity-50 text-center py-4';
      noResults.textContent = 'No currencies found';
      
      const list = dropdown.querySelector('ul[role="list"]');
      if (list) {
        list.appendChild(noResults);
      }
    } else if (!show && noResults) {
      noResults.remove();
    }
  }

  groupByCurrency(items) {
  const groups = {};
  
  items.forEach(item => {
    const link = item.querySelector('a');
    if (!link) return;
    
    const countryCode = link.dataset.value;
    const text = link.textContent.trim();
    
    // Extract currency code from text like "Germany (EUR €)"
    const currencyMatch = text.match(/\(([A-Z]{3})/);
    if (!currencyMatch) return;
    
    const currencyCode = currencyMatch[1];
    
    if (!groups[currencyCode]) {
      groups[currencyCode] = [];
    }
    
    groups[currencyCode].push({
      countryCode,
      countryName: text.split('(')[0].trim(),
      fullText: text,
      originalItem: item // Reference to the actual DOM element, not a clone
    });
  });
  
  // Sort each currency group to put the primary country first
  Object.keys(groups).forEach(currencyCode => {
    groups[currencyCode].sort((a, b) => {
      const priorityA = this.getCountryPriority(a.countryCode, currencyCode);
      const priorityB = this.getCountryPriority(b.countryCode, currencyCode);
      return priorityA - priorityB;
    });
  });
  
  return groups;
}

getCountryPriority(countryCode, currencyCode) {
    // Map of currency to preferred country code
    const primaryCountries = {
        'USD': 'US',      // United States
        'GBP': 'GB',      // United Kingdom
        'EUR': 'DE',      // Germany
        'CAD': 'CA',      // Canada
        'JPY': 'JP',      // Japan
        'AUD': 'AU',      // Australia
        'MXN': 'MX',      // Mexico
        'CZK': 'CZ',      // Czech Republic
        'CHF': 'CH',      // Switzerland
        'NOK': 'NO',      // Norway
        'SEK': 'SE',      // Sweden
        'DKK': 'DK',      // Denmark
        'PLN': 'PL',      // Poland
        'HUF': 'HU',      // Hungary
        'RON': 'RO',      // Romania
        'BGN': 'BG',      // Bulgaria
        'HRK': 'HR',      // Croatia
        'TRY': 'TR',      // Turkey
        'RUB': 'RU',      // Russia
        'BRL': 'BR',      // Brazil
        'INR': 'IN',      // India
        'CNY': 'CN',      // China
        'KRW': 'KR',      // South Korea
        'SGD': 'SG',      // Singapore
        'HKD': 'HK',      // Hong Kong
        'TWD': 'TW',      // Taiwan
        'THB': 'TH',      // Thailand
        'MYR': 'MY',      // Malaysia
        'PHP': 'PH',      // Philippines
        'IDR': 'ID',      // Indonesia
        'VND': 'VN',      // Vietnam
        'NZD': 'NZ',      // New Zealand
        'ZAR': 'ZA',      // South Africa
        'AED': 'AE',      // UAE
        'SAR': 'SA',      // Saudi Arabia
        'ILS': 'IL',      // Israel
    };
    
    // If this country is the primary for this currency, give it priority 0
    if (primaryCountries[currencyCode] === countryCode) {
        return 0;
    }
    
    // Otherwise, sort alphabetically (priority 1+)
    return 1;
    }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CurrencyOptimizer());
} else {
  new CurrencyOptimizer();
}